import { ChangeEvent, useState } from 'react'
import * as yup from 'yup'
import cn from 'classnames'
import { useToasts } from 'react-toast-notifications'
import { Formik, Form, FormikHelpers } from 'formik-latest'

import TextArea from './TextArea'
import ConsolePetitionActionButton from './ActionButton'
import { useReviewDraftMutation } from 'backoffice/components/Drafts/useDraftsQuery'

import { DraftRequestReviewAction } from 'backoffice/components/Drafts/types/types'

import styles from './ActionForm.module.scss'

interface Values {
  comment: string
}

interface FormCustomProps {
  isSubmitting: boolean
  handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  isValid: boolean
  action: DraftRequestReviewAction
  maxCommentLength: number
}

const FormCustom = (props: FormCustomProps) => {
  const { isSubmitting, handleChange, isValid, action, maxCommentLength } = props

  const actionsMap = [
    {
      action: DraftRequestReviewAction.APPROVE,
      params: {
        submitButtonClass: styles.actionApprove,
        label: 'Approve Console Petition',
      },
    },
    {
      action: DraftRequestReviewAction.FLAG,
      params: {
        submitButtonClass: styles.actionFlag,
        label: 'Flag Console Petition',
      },
    },
    {
      action: DraftRequestReviewAction.REJECT,
      params: {
        submitButtonClass: styles.actionReject,
        label: 'Reject Console Petition',
      },
    },
  ]

  const actionParams = actionsMap.find((item) => item.action === action)?.params

  const submitClassname = `submit ${actionParams?.submitButtonClass}`

  const isDisabledSubmit = isSubmitting || !isValid
  const [commentCharacterCounter, setCommentCounterCharacter] = useState(0)

  const onChangeCommentHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!e) {
      return
    }
    setCommentCounterCharacter(e.target.value.length)
    handleChange(e)
  }

  return (
    <Form className={styles.actionForm}>
      <label className={styles.label} htmlFor="comment">
        Comment (optional)
      </label>
      <TextArea
        id="comment"
        name="comment"
        className={styles.textArea}
        placeholder="insert comment"
        rows={1}
        maxLength={maxCommentLength}
        onChange={onChangeCommentHandler}
      />
      <span className={styles.commentCharacterCounter}>
        {commentCharacterCounter}/{maxCommentLength}
      </span>

      <ConsolePetitionActionButton
        className={cn(styles.submitButton, submitClassname, { [styles.disabled]: isDisabledSubmit })}
        isDisabled={isDisabledSubmit}
        onClick={() => {}}
        type="submit"
      >
        {actionParams?.label}
      </ConsolePetitionActionButton>
    </Form>
  )
}

interface ConsolePetitionActionFormProps {
  action: DraftRequestReviewAction
  draftId: number
  handleClose: () => void
}

const ConsolePetitionActionForm = ({ action, draftId, handleClose }: ConsolePetitionActionFormProps) => {
  const mutation = useReviewDraftMutation()
  const { addToast } = useToasts()

  const MAX_COMMENT_LENGTH = 1000

  const validationSchema = yup.object({
    comment: yup
      .string()
      .max(MAX_COMMENT_LENGTH, `This field can contain a maximum of ${MAX_COMMENT_LENGTH} characters`),
  })

  const initialValues = {
    comment: '',
  }

  const onSubmitHandler = async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
    try {
      await mutation.mutateAsync({
        draftId,
        action,
        comment: values.comment,
      })
      addToast('Console petition reviewed successfully', { appearance: 'success', autoDismiss: true })
      handleClose()
    } catch {
      addToast('There was a problem saving the changes', { appearance: 'error', autoDismiss: true })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
      validateOnBlur
      validateOnChange
    >
      {(props) => <FormCustom {...props} action={action} maxCommentLength={MAX_COMMENT_LENGTH} />}
    </Formik>
  )
}

export default ConsolePetitionActionForm
